import { useEffect, useMemo, useRef, useState } from "react";

import { useDirectorContext } from "@contexts/director";

import CashChart from "../../components/director/dashboard/CashChart";
import KpiStrip from "../../components/director/dashboard/KpiStrip";
import MarginTable from "../../components/director/dashboard/MarginTable";
import ObrasTable from "../../components/director/dashboard/ObrasTable";
import PremisesPanel from "../../components/director/dashboard/PremisesPanel";
import {
  COSTS_UNAVAILABLE,
  annualRate,
  blockAvailable,
  blockReason,
  monthLabel,
  percent,
  scopeRows,
} from "../../components/director/dashboard/format";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";

const SIENGE_ONLY_READING = "Leitura: só dados do Sienge. VPL, TIR e margem completa aparecem quando o valor das unidades a vender for informado.";

const rateAndTax = (data) => ` · taxa ${annualRate(data.rate?.bps)}${data.rate?.provisional ? " · taxa provisória" : ""}`
  + ` · impostos ${percent(data.taxBps)}`;

const readingLine = (data) => {
  if (data.readingMode !== "with_premises") {
    // Premissas salvas sem unidades a vender: taxa e impostos já mudam a meta, então a leitura diz quais são.
    return data.premises ? `${SIENGE_ONLY_READING}${rateAndTax(data)} (premissas salvas)` : SIENGE_ONLY_READING;
  }
  const totals = data.totals || {};
  return `Leitura: Sienge + premissas do diretor (${totals.obrasWithStock} de ${totals.obrasCount} obras com unidades a vender informadas)`
    + `${rateAndTax(data)}.`;
};

const SCOPES = [
  ["obras", "Só obras"],
  ["all", "Tudo, com despesas da empresa"],
];

const DirectorDashboard = () => {
  const { selectedCompanyId, accessByCompany = {} } = useDirectorContext();
  const access = accessByCompany[selectedCompanyId] || {};
  const [scope, setScope] = useState("obras");
  const [focus, setFocus] = useState(null);
  const [premisesOpen, setPremisesOpen] = useState(false);
  const premisesRef = useRef(null);
  const query = useMemo(() => ({ scope }), [scope]);
  const surface = useDirectorSurface("dashboard", query);
  const { data } = surface;

  const rows = useMemo(() => scopeRows(data), [data]);
  const focusKey = rows.some((row) => row.key === focus) ? focus : null;
  const withPremises = data?.readingMode === "with_premises";
  const hasPremises = withPremises || (data?.premises?.version ?? 0) > 0;
  let premisesLabel = hasPremises ? "Atualizar premissas" : "Complementar dados";
  if (premisesOpen) premisesLabel = "Fechar";

  useEffect(() => {
    if (premisesOpen) premisesRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [premisesOpen]);

  const openPremises = () => setPremisesOpen(true);

  return <ModuleShell
    className="director-dashboard"
    eyebrow={`Diretoria · ${access.companyName || "portfólio"}`}
    title="Dashboard da diretoria"
    description="As obras dão lucro? Vai faltar dinheiro nos próximos meses? Dados do Sienge com a projeção do que falta receber, do contratado e do planejado."
    badge={data?.asOf ? `Data-base ${monthLabel(data.asOf)}` : null}
    qualitySources={[surface]}
    showProjectFilter={false}
  >
    <section className="director-dashboard-bar" aria-label="Leitura e alcance">
      <div className="director-dashboard-bar__reading">
        {data && <p className="director-dashboard-bar__text">{readingLine(data)}</p>}
        <button
          className="director-link-button director-link-button--secondary director-dashboard-bar__action"
          type="button"
          aria-expanded={premisesOpen}
          aria-controls="director-dashboard-premises"
          onClick={() => setPremisesOpen((open) => !open)}
        >
          {premisesLabel}
        </button>
      </div>
      <div className="director-owner-switch" role="group" aria-label="Alcance">
        {SCOPES.map(([value, label]) => <button
          key={value}
          type="button"
          aria-pressed={scope === value}
          onClick={() => setScope(value)}
        >
          {label}
        </button>)}
      </div>
    </section>
    <ModuleState {...surface} onRetry={surface.reload} />
    {data && <>
      <KpiStrip data={data} onOpenPremises={openPremises} />
      <ObrasTable data={data} rows={rows} focus={focusKey} onFocus={setFocus} />
      {blockAvailable(data, "liquidity") && <CashChart data={data} rows={rows} focus={focusKey} onFocus={setFocus} />}
      {blockAvailable(data, "margin") && <MarginTable data={data} />}
      {blockReason(data, "margin") === "costs_unavailable" && <p className="director-dashboard-margin-unavailable">{COSTS_UNAVAILABLE}</p>}
    </>}
    {/* Fora do guarda de `data`: o painel não pode desmontar no reload nem na troca de escopo. */}
    {premisesOpen && <section
      className="director-section director-dashboard-premises"
      id="director-dashboard-premises"
      aria-label="Premissas do diretor"
      ref={premisesRef}
    >
      <header>
        <div>
          <span className="director-eyebrow">Opcional · premissas do diretor</span>
          <h2>Complementar dados</h2>
        </div>
        <p>
          Para ver VPL, TIR e margem completa de uma obra, informe o que o Sienge não tem: o valor das unidades a vender,
          o ritmo de venda, a condição de pagamento e o terreno fora do Sienge.
        </p>
      </header>
      <PremisesPanel key={selectedCompanyId} companyId={selectedCompanyId} obras={data?.obras ?? null} onSaved={surface.reload} />
    </section>}
  </ModuleShell>;
};

export default DirectorDashboard;
